import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { queryClient } from '@/app/queryClient'
import { appDependencies } from '@/app/dependencies'
import type { AuthGateway } from '../application/AuthGateway'
import type { AppSession } from '../domain/auth'
import { createFixtureAuthGateway } from '../infrastructure/fixtures/fixtureAuthGateway'
import { createSupabaseAuthGateway } from '../infrastructure/supabase/SupabaseAuthGateway'
import { AuthContext, type AuthContextValue } from './AuthContext'
import { clearZoneDrafts } from '@/shared/infrastructure/zoneDraftStorage'

function defaultGateway(): AuthGateway {
  return appDependencies.supabase
    ? createSupabaseAuthGateway(appDependencies.supabase)
    : createFixtureAuthGateway()
}

export interface AuthProviderProps extends PropsWithChildren {
  gateway?: AuthGateway
}

export function AuthProvider({ children, gateway: providedGateway }: AuthProviderProps) {
  const gateway = useMemo(() => providedGateway ?? defaultGateway(), [providedGateway])
  const [session, setSession] = useState<AppSession | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    const load = () => {
      void gateway
        .getSession()
        .then((nextSession) => {
          if (active) setSession(nextSession)
        })
        .catch(() => {
          if (active) setSession(null)
        })
        .finally(() => {
          if (active) setLoading(false)
        })
    }
    load()
    const unsubscribe = gateway.subscribe(load)
    // Un compte désactivé ou dont les sessions ont été révoquées perd l'accès côté serveur immédiatement; le
    // retour sur l'onglet relit la session pour que l'écran le dise au lieu d'afficher des erreurs.
    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      active = false
      unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [gateway])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      async signIn(credentials) {
        const nextSession = await gateway.signIn(credentials)
        setSession(nextSession)
        return nextSession
      },
      async signOut() {
        await gateway.signOut()
        queryClient.clear()
        clearZoneDrafts()
        setSession(null)
      },
      requestPasswordReset(email) {
        return gateway.requestPasswordReset(email, `${window.location.origin}/auth/reset-password`)
      },
      updatePassword(password) {
        return gateway.updatePassword(password)
      },
      async replaceTemporaryPassword(currentPassword, newPassword) {
        const nextSession = await gateway.replaceTemporaryPassword(currentPassword, newPassword)
        queryClient.clear()
        setSession(nextSession)
        return nextSession
      },
    }),
    [gateway, loading, session],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
